// Data Perusahaan, section Grup perusahaan.
//   no group   card   Buat grup (name), and the invitations addressed to me
//                     (Terima or Tolak)
//   in a group card   name (Ubah nama for owner and admin), my role, the
//                     members with their roles (Jadikan admin, anggota or
//                     pemilik for the owner; Keluarkan for owner and admin),
//                     Undang anggota by email and the open invitations
//                     (Batalkan), then Keluar dari grup (not the owner) or
//                     Hapus grup (owner), each confirmed
import { useState, type FormEvent } from "react";
import * as API from "../api";
import type { CompanyGroup as Group, CompanyGroupRole, CompanyMember, MyCompanyInvite } from "../api";
import { formatDate } from "../lib/format";
import { Menu, type MenuItem } from "../ui/Menu";
import { Badge, Dialog, EmptyState, Field, Notice, SkeletonRows, fieldAria, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";
import { companyErrorMessage, needsReload, ROLE_LABEL } from "./labels";

export interface CompanyGroupProps {
  group: Group | null;
  loading: boolean;
  error: unknown;
  /** The group after a change made here (null: left or deleted). */
  onGroup: (group: Group | null) => void;
  /** Reloads the group and the documents. */
  reloadAll: () => void;
  /** The signed-in account. */
  meId: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function CreateGroup({ onGroup, reloadAll }: Pick<CompanyGroupProps, "onGroup" | "reloadAll">) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const n = name.trim();
    if (!n) {
      setNameError("Nama grup wajib diisi.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onGroup(await API.createCompanyGroup(n));
    } catch (err) {
      setError(companyErrorMessage(err));
      if (needsReload(err)) reloadAll();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form-stack" noValidate onSubmit={submit} aria-label="Buat grup perusahaan">
      <Field id="cg-name" label="Nama grup" required error={nameError} hint="Biasanya nama perusahaan, misalnya CV Mitra Karya Mandiri.">
        <input
          {...fieldAria("cg-name", nameError)}
          value={name}
          maxLength={200}
          onChange={(e) => {
            setName(e.currentTarget.value);
            setNameError(null);
          }}
        />
      </Field>
      {error ? <Notice tone="danger" title="Grup belum dibuat">{error}</Notice> : null}
      <div className="button-row">
        <button type="submit" className="btn" aria-busy={busy || undefined} disabled={busy}>
          <Glyph name="profile2user" />
          <span>Buat grup</span>
        </button>
      </div>
    </form>
  );
}

function MyInvites({ onGroup, reloadAll }: Pick<CompanyGroupProps, "onGroup" | "reloadAll">) {
  const invites = useAsync<MyCompanyInvite[]>(() => API.getMyCompanyInvites(), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ id: string; message: string } | null>(null);

  const act = async (invite: MyCompanyInvite, accept: boolean) => {
    if (busy) return;
    setBusy(invite.id);
    setError(null);
    try {
      if (accept) onGroup(await API.acceptCompanyInvite(invite.id));
      else {
        await API.declineCompanyInvite(invite.id);
        invites.set((invites.data ?? []).filter((i) => i.id !== invite.id));
      }
    } catch (err) {
      setError({ id: invite.id, message: companyErrorMessage(err) });
      if (err instanceof API.ApiError && err.status === 404) invites.reload();
      else if (needsReload(err)) reloadAll();
    } finally {
      setBusy(null);
    }
  };

  const list = invites.data ?? [];
  return (
    <div className="form-stack" aria-labelledby="cg-invites-title">
      <h3 id="cg-invites-title" className="section-title">
        Undangan untuk Anda
      </h3>
      {invites.loading && !invites.data ? (
        <SkeletonRows count={1} label="Memuat undangan" />
      ) : invites.error ? (
        <Notice tone="danger" title="Undangan belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={invites.reload}>Muat ulang</button>}>
          <p>{companyErrorMessage(invites.error)}</p>
        </Notice>
      ) : list.length === 0 ? (
        <p className="form-note">Belum ada undangan. Minta pemilik atau admin grup perusahaan Anda mengundang email akun ini.</p>
      ) : (
        <ul className="rows" aria-label="Undangan untuk Anda">
          {list.map((i) => (
            <li key={i.id} className="row">
              <span className="row-main">
                <span className="row-title">{i.groupName}</span>
                <span className="row-meta">{[i.invitedBy ? `Diundang ${i.invitedBy.name}` : "", formatDate(i.createdAt)].filter(Boolean).join(" · ")}</span>
                {error?.id === i.id ? (
                  <span className="pk-item-error" role="alert">
                    <Glyph name="danger" size={16} />
                    <span>{error.message}</span>
                  </span>
                ) : null}
              </span>
              <span className="row-side">
                <button type="button" className="btn" disabled={busy !== null} aria-busy={busy === i.id || undefined} aria-label={`Terima undangan ${i.groupName}`} onClick={() => void act(i, true)}>
                  <Glyph name="tickCircle" />
                  <span>Terima</span>
                </button>
                <button type="button" className="btn btn-secondary" disabled={busy !== null} aria-label={`Tolak undangan ${i.groupName}`} onClick={() => void act(i, false)}>
                  <span>Tolak</span>
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type Confirm =
  | { kind: "leave" }
  | { kind: "delete" }
  | { kind: "remove"; member: CompanyMember }
  | { kind: "transfer"; member: CompanyMember };

function confirmText(c: Confirm, group: Group): { title: string; body: string; action: string } {
  switch (c.kind) {
    case "leave":
      return { title: "Keluar dari grup?", body: `Anda tidak lagi membaca dokumen ${group.name}. Dokumen yang Anda bagikan tetap milik grup.`, action: "Keluar dari grup" };
    case "delete":
      return { title: "Hapus grup?", body: `${group.name} dihapus beserta semua anggotanya, undangannya, dan dokumen yang dibagikan ke grup. Tindakan ini tidak dapat dibatalkan.`, action: "Hapus grup" };
    case "remove":
      return { title: "Keluarkan anggota?", body: `${c.member.name} tidak lagi membaca dokumen grup. Dokumen yang ia bagikan tetap milik grup.`, action: "Keluarkan" };
    case "transfer":
      return { title: "Jadikan pemilik?", body: `${c.member.name} menjadi pemilik ${group.name}. Peran Anda berubah menjadi admin.`, action: "Jadikan pemilik" };
  }
}

function InGroup({ group, onGroup, reloadAll, meId }: Omit<CompanyGroupProps, "loading" | "error"> & { group: Group }) {
  const manager = group.myRole === "owner" || group.myRole === "admin";
  const owner = group.myRole === "owner";
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(group.name);
  const [nameError, setNameError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ where: string; message: string } | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const run = async (where: string, task: () => Promise<void>) => {
    if (busy) return false;
    setBusy(where);
    setError(null);
    setSuccess(null);
    try {
      await task();
      return true;
    } catch (err) {
      setError({ where, message: companyErrorMessage(err) });
      if (needsReload(err)) reloadAll();
      return false;
    } finally {
      setBusy(null);
    }
  };

  const rename = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) {
      setNameError("Nama grup wajib diisi.");
      return;
    }
    if (n === group.name) {
      setRenaming(false);
      return;
    }
    const ok = await run("rename", async () => onGroup(await API.renameCompanyGroup(n)));
    if (ok) setRenaming(false);
  };

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL.test(value)) {
      setEmailError("Masukkan email yang valid.");
      return;
    }
    const ok = await run("invite", async () => {
      const created = await API.inviteToCompanyGroup(value);
      onGroup({ ...group, invites: [...group.invites.filter((i) => i.id !== created.id), created] });
    });
    if (ok) {
      setEmail("");
      setSuccess(`Undangan dikirim ke ${value}. Undangan muncul di Data Perusahaan akun tersebut.`);
    }
  };

  const revoke = (id: string) =>
    void run(`invite:${id}`, async () => {
      await API.revokeCompanyInvite(id);
      onGroup({ ...group, invites: group.invites.filter((i) => i.id !== id) });
    });

  const setRole = (m: CompanyMember, role: CompanyGroupRole) => void run(`member:${m.userId}`, async () => onGroup(await API.setCompanyMemberRole(m.userId, role)));

  const confirmed = async () => {
    if (!confirm) return;
    const c = confirm;
    const where = c.kind === "remove" || c.kind === "transfer" ? `member:${c.member.userId}` : c.kind;
    const ok = await run(where, async () => {
      if (c.kind === "leave") {
        await API.leaveCompanyGroup();
        onGroup(null);
      } else if (c.kind === "delete") {
        await API.deleteCompanyGroup();
        onGroup(null);
      } else if (c.kind === "remove") {
        await API.removeCompanyMember(c.member.userId);
        onGroup({ ...group, members: group.members.filter((m) => m.userId !== c.member.userId) });
      } else {
        onGroup(await API.setCompanyMemberRole(c.member.userId, "owner"));
      }
    });
    if (ok || c.kind === "remove" || c.kind === "transfer") setConfirm(null);
  };

  const memberActions = (m: CompanyMember): MenuItem[] => {
    if (m.userId === meId || m.role === "owner") return [];
    const items: MenuItem[] = [];
    if (owner) {
      if (m.role !== "admin") items.push({ id: "admin", label: "Jadikan admin", icon: "shieldTick", onSelect: () => setRole(m, "admin") });
      if (m.role !== "member") items.push({ id: "member", label: "Jadikan anggota", icon: "profileCircle", onSelect: () => setRole(m, "member") });
      items.push({ id: "owner", label: "Jadikan pemilik", icon: "building", onSelect: () => setConfirm({ kind: "transfer", member: m }) });
    }
    // An admin removes members only; the owner anyone but itself.
    if (owner || (manager && m.role === "member")) items.push({ id: "remove", label: "Keluarkan", icon: "logout", tone: "danger", onSelect: () => setConfirm({ kind: "remove", member: m }) });
    return items;
  };

  const errorAt = (where: string) =>
    error?.where === where ? (
      <span className="pk-item-error" role="alert">
        <Glyph name="danger" size={16} />
        <span>{error.message}</span>
      </span>
    ) : null;

  const text = confirm ? confirmText(confirm, group) : null;
  const confirmBusy = busy !== null && confirm !== null;
  const confirmWhere = confirm ? (confirm.kind === "remove" || confirm.kind === "transfer" ? `member:${confirm.member.userId}` : confirm.kind) : "";

  return (
    <>
      {renaming ? (
        <form className="form-stack" noValidate onSubmit={rename} aria-label="Ubah nama grup">
          <Field id="cg-rename" label="Nama grup" required error={nameError}>
            <input
              {...fieldAria("cg-rename", nameError)}
              value={name}
              maxLength={200}
              onChange={(e) => {
                setName(e.currentTarget.value);
                setNameError(null);
              }}
            />
          </Field>
          {errorAt("rename")}
          <div className="button-row">
            <button type="submit" className="btn" aria-busy={busy === "rename" || undefined} disabled={busy !== null}>
              Simpan nama
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => setRenaming(false)}>
              Batal
            </button>
          </div>
        </form>
      ) : (
        <div className="section-head">
          <div className="section-head-text">
            <p className="row-title" id="cg-group-name">
              {group.name}
            </p>
            <p className="section-lead">
              <Badge tone="info">Peran Anda: {ROLE_LABEL[group.myRole]}</Badge> Dibuat {formatDate(group.createdAt)}
            </p>
          </div>
          {manager ? (
            <div className="section-action">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setName(group.name);
                  setNameError(null);
                  setRenaming(true);
                }}
              >
                <Glyph name="edit2" />
                <span>Ubah nama</span>
              </button>
            </div>
          ) : null}
        </div>
      )}

      <h3 className="section-title" id="cg-members-title">
        Anggota ({group.members.length})
      </h3>
      <ul className="rows" aria-labelledby="cg-members-title">
        {group.members.map((m) => {
          const items = manager ? memberActions(m) : [];
          return (
            <li key={m.userId} className="row" data-role={m.role}>
              <span className="row-main">
                <span className="row-title">
                  {m.name}
                  {m.userId === meId ? " (Anda)" : ""}
                </span>
                <span className="row-meta">
                  <Badge tone={m.role === "member" ? "neutral" : "info"}>{ROLE_LABEL[m.role]}</Badge> {m.email} · bergabung {formatDate(m.joinedAt)}
                </span>
                {errorAt(`member:${m.userId}`)}
              </span>
              {items.length ? (
                <span className="row-side">
                  <Menu label={`Aksi untuk ${m.name}`} items={items} />
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>

      {manager ? (
        <>
          <form className="form-stack" noValidate onSubmit={invite} aria-label="Undang anggota">
            <Field id="cg-invite" label="Undang anggota" error={emailError} hint="Masukkan email akun Pluto rekan satu perusahaan. Ia menerima undangan dari halaman Data Perusahaan.">
              <input
                {...fieldAria("cg-invite", emailError)}
                type="email"
                inputMode="email"
                autoComplete="off"
                value={email}
                placeholder="nama@perusahaan.co.id"
                onChange={(e) => {
                  setEmail(e.currentTarget.value);
                  setEmailError(null);
                }}
              />
            </Field>
            {errorAt("invite")}
            {success ? <Notice tone="success" title="Undangan dikirim">{success}</Notice> : null}
            <div className="button-row">
              <button type="submit" className="btn btn-secondary" aria-busy={busy === "invite" || undefined} disabled={busy !== null}>
                <Glyph name="send" />
                <span>Kirim undangan</span>
              </button>
            </div>
          </form>

          <h3 className="section-title" id="cg-open-title">
            Undangan terbuka
          </h3>
          {group.invites.length === 0 ? (
            <p className="form-note">Tidak ada undangan yang menunggu.</p>
          ) : (
            <ul className="rows" aria-labelledby="cg-open-title">
              {group.invites.map((i) => (
                <li key={i.id} className="row">
                  <span className="row-main">
                    <span className="row-title">{i.email}</span>
                    <span className="row-meta">{[i.invitedBy ? `Diundang ${i.invitedBy.name}` : "", formatDate(i.createdAt)].filter(Boolean).join(" · ")}</span>
                    {errorAt(`invite:${i.id}`)}
                  </span>
                  <span className="row-side">
                    <button type="button" className="btn btn-secondary" disabled={busy !== null} aria-busy={busy === `invite:${i.id}` || undefined} aria-label={`Batalkan undangan ${i.email}`} onClick={() => revoke(i.id)}>
                      Batalkan
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {errorAt("leave")}
      {errorAt("delete")}
      <div className="button-row">
        {owner ? (
          <button type="button" className="btn btn-secondary" data-tone="danger" disabled={busy !== null} onClick={() => setConfirm({ kind: "delete" })}>
            <Glyph name="trash" />
            <span>Hapus grup</span>
          </button>
        ) : (
          <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => setConfirm({ kind: "leave" })}>
            <Glyph name="logout" />
            <span>Keluar dari grup</span>
          </button>
        )}
      </div>

      <Dialog
        open={confirm !== null}
        title={text?.title ?? ""}
        description={text?.body}
        onClose={() => setConfirm(null)}
        busy={confirmBusy}
        size="sm"
        footer={
          <>
            <button type="button" className="btn btn-secondary" disabled={confirmBusy} onClick={() => setConfirm(null)}>
              Batal
            </button>
            <button type="button" className="btn" data-tone={confirm?.kind === "transfer" ? undefined : "danger"} aria-busy={busy === confirmWhere || undefined} disabled={confirmBusy} onClick={() => void confirmed()}>
              {text?.action}
            </button>
          </>
        }
      >
        {error && error.where === confirmWhere ? <Notice tone="danger" title="Belum berhasil">{error.message}</Notice> : <p className="form-note">Pastikan pilihan ini sudah benar.</p>}
      </Dialog>
    </>
  );
}

export function CompanyGroupSection({ group, loading, error, onGroup, reloadAll, meId }: CompanyGroupProps) {
  return (
    <section className="card" aria-labelledby="cg-title">
      <div className="section-head">
        <div className="section-head-text">
          <h2 id="cg-title" className="card-title">
            Grup perusahaan
          </h2>
          <p className="section-lead">
            {group ? "Anggota grup berbagi dokumen perusahaan yang dibagikan, dan Pluto memakainya untuk setiap anggota." : "Buat grup untuk berbagi dokumen perusahaan dengan rekan satu perusahaan, atau terima undangan dari grup yang sudah ada."}
          </p>
        </div>
      </div>
      {loading && !group ? (
        <SkeletonRows count={2} label="Memuat grup perusahaan" />
      ) : error ? (
        <Notice tone="danger" title="Grup perusahaan belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={reloadAll}>Muat ulang</button>}>
          <p>{companyErrorMessage(error)}</p>
        </Notice>
      ) : group ? (
        <InGroup key={group.id} group={group} onGroup={onGroup} reloadAll={reloadAll} meId={meId} />
      ) : (
        <>
          <EmptyState icon="profile2user" title="Anda belum tergabung dalam grup" body="Tanpa grup, dokumen perusahaan hanya dipakai untuk akun Anda sendiri." />
          <CreateGroup onGroup={onGroup} reloadAll={reloadAll} />
          <MyInvites onGroup={onGroup} reloadAll={reloadAll} />
        </>
      )}
    </section>
  );
}
